import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Edit, Delete } from "@mui/icons-material";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { getToken, base_url } from "../../config/config";

// Define the list of employees
// const employees = [
//   "Michael Otte",
//   "Daniel Herzog",
//   "Ronald Hasche",
//   "Rico Rasche",
//   "Jens Schubert",
//   "Jörg Melms",
//   "Maik Heinig",
//   "Marino Uhlemann",
//   "Jörg Maschner",
//   "Daniel Reddel",
// ];

const UberStundens = () => {
  const [apiData, setApiData] = useState([]);
  const [refreshId, setRefreshId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [filterANR, setFilterANR] = useState("");
  const [overtimeFilter, setOvertimeFilter] = useState("");
  const [editData, setEditData] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error' | 'warning' | 'info'
  });
  const [newData, setNewData] = useState({
    user: "",
    ANR: "",
    description: "",
    date: "",
    StartTime: "",
    EndTime: "",
    Arbeiten: "",
    isOverTime: false,
    UberStundenStatus: "",
  });
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const token = getToken();
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${base_url}/v1/getAllEmployees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data && Array.isArray(response.data)) {
          setEmployees(response.data);
        } else {
          console.error("Unexpected employee data format:", response.data);
          setSnackbar({
            open: true,
            message: response?.data?.message || "Fehler beim Abrufen der Daten",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        setSnackbar({
          open: true,
          message: error.message || "Fehler beim Abrufen der Daten",
          severity: "error",
        });
      }
    };

    fetchEmployees();
  }, []);

  // Dropdown component for selecting employees
  const EmployeeSelect = ({ value, onChange }) => (
    <FormControl fullWidth>
      <InputLabel>Employee</InputLabel>
      <Select value={value} onChange={onChange} label="Employee">
        {employees.map((employee) => (
          <MenuItem key={employee.name} value={employee.name}>
            {employee.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  // Fetch API data
  useEffect(() => {
    axios
      .get(`${base_url}/v1/getAllUserPendingProjects`)
      .then((response) => {
        if (response.data.status === 1) {
          setApiData(response.data.data);
        } else {
          console.error("Error fetching API data:", response.data.message);
        }
      })
      .catch((error) => {
        console.error("Error fetching API data:", error);
      });
  }, [refreshId]);

  // Calculate time difference
  const calculateTotalTime = (startTime, endTime) => {
    if (!startTime || !endTime) return "Pending";

    // Function to parse time and default missing parts to zero
    const parseTime = (timeStr) => {
      const [hours = "0", minutes = "0", seconds = "0"] = timeStr.split(":");
      return new Date(
        1970,
        0,
        1,
        Number(hours),
        Number(minutes),
        Number(seconds)
      );
    };

    const startDate = parseTime(startTime);
    const endDate = parseTime(endTime);

    console.log("Start Time:", startTime, "Parsed Start Date:", startDate);
    console.log("End Time:", endTime, "Parsed End Date:", endDate);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()))
      return "Pending";

    // Adjust endDate if it's before startDate
    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1); // Assume end time could be on the next day
    }

    const diffMs = endDate - startDate;

    if (isNaN(diffMs) || diffMs <= 0) return "Pending";

    const diffHrs = Math.floor(diffMs / 3600000);
    const diffMins = Math.floor((diffMs % 3600000) / 60000);

    return `${diffHrs}h ${diffMins}m`;
  };

  // Filter data based on date range, user, ANR, and overtime
  useEffect(() => {
    const filtered = apiData
      .map((project) => {
        const totalTime = calculateTotalTime(
          project.StartTime,
          project.EndTime
        );

        return {
          ...project,
          TotalTime: totalTime,
        };
      })
      .filter((project) => {
        const projectDate = new Date(project.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        const matchesDateRange =
          (!start || projectDate >= start) && (!end || projectDate <= end);
        const matchesUser =
          !filterUser ||
          (project.user &&
            project.user.toLowerCase().includes(filterUser.toLowerCase()));
        const matchesANR =
          !filterANR ||
          (project.ANR && project.ANR.toString().includes(filterANR));
        const matchesOvertime =
          !overtimeFilter ||
          (overtimeFilter === "Ja" ? project.isOverTime : !project.isOverTime);

        return matchesDateRange && matchesUser && matchesANR && matchesOvertime;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredData(filtered);
  }, [startDate, endDate, apiData, filterUser, filterANR, overtimeFilter]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "user",
        header: "Mitarbeiter",
        size: 150,
      },
      {
        accessorKey: "ANR",
        header: "ANR",
        size: 20,
      },
      {
        accessorKey: "Menge",
        header: "Menge",
        size: 20,
      },
      {
        accessorKey: "description",
        header: "Beschreibung",
        size: 150,
      },
      {
        accessorKey: "date",
        header: "Date",
        size: 20,
      },
      {
        accessorKey: "StartTime",
        header: "Startzeit",
        size: 100,
      },
      {
        accessorKey: "EndTime",
        header: "Endzeit",
        size: 100,
      },
      {
        accessorKey: "TotalTime",
        header: "Gesamtzeit",
        size: 100,
      },

      {
        accessorKey: "Arbeiten",
        header: "Arbeiten",
        size: 300,
      },
      {
        accessorKey: "isOverTime",
        header: "Überstunden",
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? "Ja" : "Nein"),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => (
          <>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditOpen(row.original)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Delete />}
              onClick={() => handleDelete(row.original._id)}
            >
              Delete
            </Button>
          </>
        ),
      },
    ],
    []
  );

  // Handle export to Excel
  const handleExport = () => {
    const visibleData = filteredData.map((row) => ({
      user: row.user,
      ANR: row.ANR,
      description: row.description,
      date: row.date,
      StartTime: row.StartTime,
      EndTime: row.EndTime,
      TotalTime: row.TotalTime,
      Arbeiten: row.Arbeiten,
      isOverTime: row.isOverTime ? "Ja" : "Nein",
    }));

    const worksheet = XLSX.utils.json_to_sheet(visibleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Projects");
    XLSX.writeFile(workbook, "filtered_projects.xlsx");
  };

  // Handle opening of edit modal
  const handleEditOpen = (rowData) => {
    setEditData(rowData);
    setIsEditOpen(true);
  };

  // Handle closing of edit modal
  const handleEditClose = () => {
    setIsEditOpen(false);
  };

  // Handle saving of edited data
  const handleEditSave = async () => {
    try {
      const response = await axios.post(
        `${base_url}/v1/updateProjectbyId`,
        editData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setApiData((prevData) =>
          prevData.map((project) =>
            project._id === editData._id ? editData : project
          )
        );
        setIsEditOpen(false);
        setRefreshId(Date.now()); // Trigger data refresh
      } else {
        console.error("Error updating project:", response.data.message);
      }
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  // Handle opening of add modal
  const handleAddOpen = () => {
    setIsAddOpen(true);
  };

  // Handle closing of add modal
  const handleAddClose = () => {
    setIsAddOpen(false);
  };

  // Handle saving of new data
  const handleAddSave = async () => {
    try {
      const response = await axios.post(
        `${base_url}/v1/createUserProject`,
        newData
      );
      if (response.data.status === 1) {
        setApiData([...apiData, response.data.data]);
        setIsAddOpen(false);
        setRefreshId(Date.now()); // Trigger data refresh
      } else {
        console.error("Error adding project:", response.data.message);
      }
    } catch (error) {
      console.error("Error adding project:", error);
    }
  };

  // Handle deletion of a project
  const handleDelete = async (id) => {
    try {
      console.log("id", id);
      const response = await axios.delete(
        `${base_url}/v1/deleteUserProject/${id}`
      );
      if (response.data.status === 1) {
        setApiData((prevData) =>
          prevData.filter((project) => project._id !== id)
        );
        setRefreshId(Date.now()); // Trigger data refresh
      } else {
        console.error("Error deleting project:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  };

  return (
    <div style={{ margin: "20px" }}>
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label="Filter Mitarbeiter"
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        />
        <TextField
          label="Filter ANR"
          value={filterANR}
          onChange={(e) => setFilterANR(e.target.value)}
        />

        <Button variant="contained" color="primary" onClick={handleExport}>
          Export Filtered Data to Excel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleAddOpen}
          style={{ marginBottom: "20px" }}
        >
          Add New Row
        </Button>
      </div>

      <div>
        <Typography>Überstunden</Typography>
        <ToggleButtonGroup
          value={overtimeFilter}
          exclusive
          onChange={(e, newFilter) => setOvertimeFilter(newFilter)}
          aria-label="Überstunden Filter"
        >
          <ToggleButton value="Ja" aria-label="Ja">
            Ja
          </ToggleButton>
          <ToggleButton value="Nein" aria-label="Nein">
            Nein
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div
        id="table-container"
        style={{ overflow: "hidden", marginBottom: "20px" }}
      >
        <MaterialReactTable columns={columns} data={filteredData} />
      </div>

      {/* Edit modal */}
      <Dialog open={isEditOpen} onClose={handleEditClose}>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          {editData && (
            <form>
              {/*   
  <EmployeeSelect
                  value={editData.user}
                  onChange={(e) => setEditData({ ...editData, user: e.target.value })}
                /> */}
              <TextField
                margin="dense"
                label="ANR"
                fullWidth
                value={editData.ANR}
                onChange={(e) =>
                  setEditData({ ...editData, ANR: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Beschreibung"
                fullWidth
                value={editData.description}
                onChange={(e) =>
                  setEditData({ ...editData, description: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Date"
                type="date"
                fullWidth
                value={editData.date}
                onChange={(e) =>
                  setEditData({ ...editData, date: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="dense"
                label="Startzeit"
                type="time"
                fullWidth
                value={editData.StartTime}
                onChange={(e) =>
                  setEditData({ ...editData, StartTime: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Endzeit"
                type="time"
                fullWidth
                value={editData.EndTime}
                onChange={(e) =>
                  setEditData({ ...editData, EndTime: e.target.value })
                }
              />
              <TextField
                margin="dense"
                label="Arbeiten"
                fullWidth
                value={editData.Arbeiten}
                onChange={(e) =>
                  setEditData({ ...editData, Arbeiten: e.target.value })
                }
              />
              <label>Überstunden-Genehmigung</label>
              <ToggleButtonGroup
                value={editData.UberStundenStatus ? "approved" : "pending"}
                exclusive
                onChange={(e, newFilter) =>
                  setEditData({
                    ...editData,
                    UberStundenStatus: newFilter === "approved",
                  })
                }
                aria-label="Überstunden"
                fullWidth
              >
                <ToggleButton value="approved" aria-label="approved">
                  Genehmigt
                </ToggleButton>
                <ToggleButton value="pending" aria-label="pending">
                  Missbilligen
                </ToggleButton>
              </ToggleButtonGroup>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleEditSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add modal */}
      <Dialog open={isAddOpen} onClose={handleAddClose}>
        <DialogTitle>Add New Project</DialogTitle>
        <DialogContent>
          <form>
            <EmployeeSelect
              value={newData.user}
              onChange={(e) => setNewData({ ...newData, user: e.target.value })}
            />
            <TextField
              margin="dense"
              label="ANR"
              fullWidth
              value={newData.ANR}
              onChange={(e) => setNewData({ ...newData, ANR: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Beschreibung"
              fullWidth
              value={newData.description}
              onChange={(e) =>
                setNewData({ ...newData, description: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Date"
              type="date"
              fullWidth
              value={newData.date}
              onChange={(e) => setNewData({ ...newData, date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              margin="dense"
              label="Startzeit"
              type="time"
              fullWidth
              value={newData.StartTime}
              onChange={(e) =>
                setNewData({ ...newData, StartTime: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Endzeit"
              type="time"
              fullWidth
              value={newData.EndTime}
              onChange={(e) =>
                setNewData({ ...newData, EndTime: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Arbeiten"
              fullWidth
              value={newData.Arbeiten}
              onChange={(e) =>
                setNewData({ ...newData, Arbeiten: e.target.value })
              }
            />
            <ToggleButtonGroup
              value={newData.isOverTime ? "Ja" : "Nein"}
              exclusive
              onChange={(e, newFilter) =>
                setNewData({ ...newData, isOverTime: newFilter === "Ja" })
              }
              aria-label="Überstunden"
              fullWidth
            >
              <ToggleButton value="Ja" aria-label="Ja">
                Ja
              </ToggleButton>
              <ToggleButton value="Nein" aria-label="Nein">
                Nein
              </ToggleButton>
            </ToggleButtonGroup>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UberStundens;
